<template>
  <section class="pc-panel pc-panel--list">
    <header class="pc-panel__head">
      <span class="pc-panel__bar"></span>
      <h2 class="pc-panel__title">我的订单</h2>
    </header>

    <div class="pc-order-tabs">
      <el-tabs v-model="activeTab" @tab-click="onTabChange">
        <el-tab-pane
          v-for="tab in tabs"
          :key="tab.key"
          :name="tab.key"
        >
          <span slot="label" class="pc-tabs__label">
            {{ tab.label }}
            <span
              v-if="tab.key === 'pending' && pendingCount > 0"
              class="pc-tabs__badge"
            >
              {{ pendingCount }}
            </span>
          </span>
        </el-tab-pane>
      </el-tabs>
    </div>

    <div v-if="loading" class="pc-empty pc-empty--placeholder">加载中...</div>
    <div
      v-else-if="filteredOrders.length === 0"
      class="pc-empty pc-empty--placeholder"
    >
      <img class="pc-empty__img" :src="emptyPlaceholder" alt="" />
      <p class="pc-empty__text">暂无数据</p>
    </div>
    <ul v-else class="pc-order-list indiv-scrollbar">
      <li
        v-for="order in filteredOrders"
        :key="order.orderNo || order.orderId"
        class="pc-order"
        :class="{ 'is-expired': isExpired(order) }"
      >
        <div class="pc-order__top">
          <div class="pc-order__head-left">
            <span
              class="pc-order__status"
              :class="statusClass(order.payStatus)"
            >
              {{ order.payStatusText || payStatusText(order.payStatus) }}
            </span>
            <div class="pc-order__no-line">
              <span class="pc-order__no">{{ order.orderNo }}</span>
              <span
                v-if="Number(order.payStatus) === PAY_STATUS.PENDING"
                class="pc-order__countdown"
              >
                请在
                <em style="color: #F14545;">{{ countdownText(order) }}</em>
                内支付，否则订单将自动取消
              </span>
            </div>
          </div>
          <div class="pc-order__actions">
            <button
              v-if="Number(order.payStatus) === PAY_STATUS.PENDING"
              type="button"
              class="pc-order__pay"
              @click="$emit('pay', order)"
            >
              立即支付
            </button>
            <button
              v-if="Number(order.payStatus) === PAY_STATUS.PENDING"
              type="button"
              class="pc-order__detail"
              :disabled="cancelling"
              @click="openCancelDialog(order)"
            >
              取消订单
            </button>
            <button
              v-else-if="!isExpired(order)"
              type="button"
              class="pc-order__detail"
              @click="toggleDetail(order.orderNo)"
            >
              详情
              <i
                class="el-icon-arrow-down"
                :class="{ 'is-expanded': isDetailExpanded(order) }"
              ></i>
            </button>
          </div>
        </div>
        <div class="pc-order__meta">
          <span class="pc-order__meta-item">
            <label>支付金额：</label>
            <em>￥{{ formatAmount(order.amount) }}</em>
          </span>
          <span class="pc-order__meta-item">
            <label>购买年限：</label>
            <b>{{ yearsLabel(order) }}</b>
          </span>
          <span class="pc-order__meta-item">
            <label>购买时间：</label>
            <b>{{ order.createTime || '—' }}</b>
          </span>
        </div>
        <el-collapse-transition>
          <div
            v-show="isDetailExpanded(order)"
            class="pc-order__extra"
          >
            <div class="pc-order__meta pc-order__meta--extra">
              <span class="pc-order__meta-item">
                <label>订单类型：</label>
                <b>{{ order.orderTypeText || '—' }}</b>
              </span>
              <span class="pc-order__meta-item">
                <label>支付渠道：</label>
                <b>{{ payChannelText(order.payChannel) }}</b>
              </span>
            </div>
          </div>
        </el-collapse-transition>
      </li>
    </ul>

    <CancelOrderDialog
      :visible.sync="cancelDialogVisible"
      :order="cancelTarget"
      :cancelling="cancelling"
      @close="onCancelDialogClose"
      @confirm="confirmCancel"
    />
  </section>
</template>

<script>
/** 我的订单：tab 筛选 + 列表展示，支付/详情由父组件处理 */
import CollapseTransition from 'element-ui/lib/transitions/collapse-transition';
import { ORDER_TABS, PAY_STATUS } from '../../config';
import { fetchOrderList, cancelOrder } from '../../api';
import emptyPlaceholder from '../../img/zw.png';
import CancelOrderDialog from './CancelOrderDialog.vue';

/** 列表字段：orderId/orderNo/years/amount/payStatus/payStatusText/payChannel/orderType/orderTypeText/createTime/autoCancelTime */
const YEAR_LABELS = {
  1: '一年',
  2: '二年',
  3: '三年',
  4: '四年',
  5: '五年',
};

export default {
  name: 'PcMyOrders',
  components: {
    CancelOrderDialog,
    ElCollapseTransition: CollapseTransition,
  },
  data() {
    return {
      tabs: ORDER_TABS,
      activeTab: 'all',
      orders: [],
      loading: false,
      expandedNo: '',
      now: Date.now(),
      countdownTimer: null,
      PAY_STATUS,
      emptyPlaceholder,
      cancelDialogVisible: false,
      cancelTarget: null,
      cancelling: false,
    };
  },
  computed: {
    /** 按当前 tab 的 payStatus 筛选订单，「全部」不过滤 */
    filteredOrders() {
      const tab = this.tabs.find((t) => t.key === this.activeTab);
      if (!tab || tab.payStatus == null) return this.orders;
      return this.orders.filter(
        (o) => Number(o.payStatus) === tab.payStatus
      );
    },
    /** 待支付数量，用于 tab 角标 */
    pendingCount() {
      return this.orders.filter(
        (o) => Number(o.payStatus) === PAY_STATUS.PENDING
      ).length;
    },
  },
  mounted() {
    this.loadOrders();
    this.countdownTimer = setInterval(() => {
      this.now = Date.now();
    }, 100);
  },
  beforeDestroy() {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
  },
  methods: {
    /** 切换 tab 时刷新订单列表 */
    onTabChange() {
      this.expandedNo = '';
      this.loadOrders();
    },
    /** GET /payment/order/list */
    loadOrders() {
      this.loading = true;
      fetchOrderList()
        .then((list) => {
          this.orders = list || [];
        })
        .finally(() => {
          this.loading = false;
        });
    },
    /** 待支付、已取消订单默认展开详情 */
    isDetailExpanded(order) {
      if (Number(order.payStatus) === PAY_STATUS.PENDING) return true;
      if (this.isExpired(order)) return true;
      return this.expandedNo === order.orderNo;
    },
    /** 展开/收起订单详情 */
    toggleDetail(orderNo) {
      this.expandedNo = this.expandedNo === orderNo ? '' : orderNo;
    },
    openCancelDialog(order) {
      this.cancelTarget = order;
      this.cancelDialogVisible = true;
    },
    onCancelDialogClose() {
      if (!this.cancelling) {
        this.cancelTarget = null;
      }
    },
    confirmCancel() {
      const order = this.cancelTarget;
      if (!order || this.cancelling) return;
      const orderId = order.orderId;
      if (!orderId) return;
      this.cancelling = true;
      cancelOrder(orderId)
        .then(() => {
          this.cancelDialogVisible = false;
          this.cancelTarget = null;
          this.loadOrders();
        })
        .catch(() => {})
        .finally(() => {
          this.cancelling = false;
        });
    },
    /** 已失效订单（payStatus=2） */
    isExpired(order) {
      return Number(order.payStatus) === PAY_STATUS.CANCELLED;
    },
    /** payStatus 转中文（接口未返回 payStatusText 时使用） */
    payStatusText(status) {
      const map = {
        [PAY_STATUS.PENDING]: '待支付',
        [PAY_STATUS.PAID]: '已支付',
        [PAY_STATUS.CANCELLED]: '已失效',
        [PAY_STATUS.REFUNDED]: '已退款',
      };
      return map[Number(status)] || '—';
    },
    /** 订单状态标签样式：待支付红 / 已支付蓝 / 其他灰 */
    statusClass(status) {
      const s = Number(status);
      if (s === PAY_STATUS.PENDING) return 'is-pending';
      if (s === PAY_STATUS.PAID) return 'is-paid';
      return 'is-muted';
    },
    formatAmount(amount) {
      if (amount == null || amount === '') return '0.00';
      return String(amount);
    },
    yearsLabel(order) {
      const years = Number(order.years);
      if (!years) return '—';
      return YEAR_LABELS[years] || `${years}年`;
    },
    payChannelText(channel) {
      const map = {
        alipay: '支付宝',
        wxpay: '微信支付',
      };
      return map[channel] || channel || '—';
    },
    /** 待支付时接口返回 autoCancelTime，其他状态为 null */
    orderExpireTime(order) {
      if (!order.autoCancelTime) return 0;
      const end = new Date(
        String(order.autoCancelTime).replace(/-/g, '/')
      ).getTime();
      return Number.isNaN(end) ? 0 : end;
    },
    countdownText(order) {
      const end = this.orderExpireTime(order);
      if (!end) return '—';
      const diff = end - this.now;
      if (diff <= 0) return '0时0分';
      const totalMin = Math.floor(diff / 60000);
      const hour = Math.floor(totalMin / 60);
      const min = totalMin % 60;
      return `${hour}时${min}分`;
    },
  },
};
</script>

<style scoped src="./style.css"></style>


// WEBPACK FOOTER //
// src/view/client/personal-center/panels/MyOrders/index.vue